import { ItemService } from 'src/app/services/item.service';
import { StakeholderService } from 'src/app/services/stakeholder.service';
import { ResultsService } from 'src/app/services/results.service';
import { Component, inject, input, output, viewChild } from '@angular/core';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { ResultRowModel } from 'src/app/models/result-table-row.model';
import { Stakeholder } from 'src/app/models/stakeholder.model';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ItemForm } from 'src/app/models/item-form.model';
import { pairwise, startWith } from 'rxjs';
import { EvaluationService } from 'src/app/services/evaluation.service';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { convertScoreToPlusMinusScore } from 'src/app/utils/item.util';
import { NgClass } from '@angular/common';
import { CustomIconComponent } from '../../../../shared-modules/custom-icon/custom-icon.component';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';

@Component({
    selector: 'app-results-table',
    templateUrl: './results-table.component.html',
    styleUrls: ['./results-table.component.scss'],
    imports: [NgClass, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, CustomIconComponent, MatFormField, MatSelect, ReactiveFormsModule, MatOption, MatInput, CdkTextareaAutosize, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow]
})
export class ResultsTableComponent {
  private resultsService = inject(ResultsService);
  private stakeholderService = inject(StakeholderService);
  private itemService = inject(ItemService);
  private evaluationService = inject(EvaluationService);
  private formBuilder = inject(FormBuilder);

  public displayedColumns: string[] = [];
  public dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  public stakeholders: Stakeholder[] = [];

  public data: ResultRowModel[] = [];
  public rowForms: FormArray;

  readonly isCurrentSituation = input.required<boolean>();
  readonly isPdfExport = input<boolean>(false);

  readonly dataUpdated = output<ItemForm>({ alias: 'dataUpdated' });

  readonly autosize = viewChild.required<CdkTextareaAutosize>('autosize');

  constructor() {

    this.rowForms = this.formBuilder.array([])

    this.stakeholderService.getStakeholders().subscribe(stakeholders => {
      this.stakeholders = stakeholders;

      // create columns for each stakeholder dynamically
      const stakeholderColumns = this.stakeholders.map(stakeholder => `evaluation.${stakeholder.userId}`);
      this.displayedColumns = this.isPdfExport() ? ['itemNamePDF', 'finalScorePDF', 'finalUnderpinningPDF'] : ['itemName', ...stakeholderColumns, 'avgEvaluation', 'finalScore', 'finalUnderpinning'];

      this.resultsService.getResults(this.isCurrentSituation()).subscribe(results => {
        const filteredResults = results.filter(result => result.enabled);
        this.dataSource = new MatTableDataSource<ResultRowModel>(filteredResults);
        const itemFormArray: FormArray = this.formBuilder.array([]);

        filteredResults.forEach(row => {
          itemFormArray.push(this.getItemFormGroup(row))
        });

        this.rowForms = itemFormArray;
        this.dataSource.data = this.rowForms.controls

        // each row gets its own valuechanges event subscriber
        this.rowForms.controls.forEach((control) => {
          control.valueChanges
            .pipe(startWith(control.value), pairwise())
            .subscribe(([prevValue, newValue]) => {
              this.detectChanges(prevValue, newValue)
            });
        });
      });
    });

  }

  public convertScoreToPlusMinusScore(score: number, concensus = false): string {
    return convertScoreToPlusMinusScore(score, concensus);
  }

  private getItemFormGroup(row: ResultRowModel): FormGroup {
    const itemGroup: FormGroup = this.formBuilder.group({});

    const rowWithDefaultedValues = {
      ...row,
      finalScore: row.finalScore ?? 0
    }

    Object.entries(rowWithDefaultedValues).forEach(([key, value]) => {
      itemGroup.addControl(key, this.formBuilder.control(value, { updateOn: 'blur' }));
    });

    return itemGroup;
  }

  private detectChanges(prevValue: any, newValue: any) {
    if (prevValue.userId !== newValue.userId) {
      this.itemService.updateItemStakeholder(newValue.itemId, newValue.userId).subscribe();
    }

    if (prevValue.finalScore !== newValue.finalScore || prevValue.finalUnderPinning !== newValue.finalUnderPinning) {
      this.evaluationService.upsertFinalEvaluation({
        itemId: newValue.itemId,
        score: newValue.finalScore,
        underpinning: newValue.finalUnderPinning
      }).subscribe();
    }
  }
}
